// src/components/HelpCenter.jsx
import React, { useMemo, useState } from "react";

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const header = useMemo(
    () => ({
      badge: "Support Center",
      title: "How Can We Help You?",
      description: "Find answers to common questions or contact our support team.",
    }),
    []
  );

  const categories = useMemo(
    () => [
      { id: "all", name: "All Topics" },
      { id: "orders", name: "Orders" },
      { id: "shipping", name: "Shipping" },
      { id: "returns", name: "Returns" },
      { id: "payment", name: "Payment" },
      { id: "account", name: "Account" },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        category: "orders",
        question: "How do I track my order?",
        answer:
          "You can track your order using the tracking number sent to your email. Click the tracking link or enter the tracking number on our Track Order page. Updates usually appear within 24 hours of shipment.",
      },
      {
        category: "orders",
        question: "Can I cancel or modify my order?",
        answer:
          "Orders can be cancelled or modified only if they have not yet shipped. Please contact us as soon as possible with your order number. After shipment, you may need to follow our return process.",
      },
      {
        category: "orders",
        question: "I did not receive an order confirmation.",
        answer:
          "Please check your spam or junk folder first. If you still cannot find it, log into your account to check your order history or contact us with the email address used during checkout.",
      },
      {
        category: "shipping",
        question: "What is the delivery charge?",
        answer:
          "We currently charge a flat delivery fee of $6.99 for eligible delivery locations within the United States.",
      },
      {
        category: "shipping",
        question: "Do you ship internationally?",
        answer:
          "No. We currently ship within the United States only. International shipping is not available at this time.",
      },
      {
        category: "shipping",
        question: "How long does delivery take?",
        answer:
          "Estimated delivery time may vary depending on your delivery location, carrier availability, weather conditions, holidays, and other factors beyond our control.",
      },
      {
        category: "shipping",
        question: "My package shows delivered but I did not receive it.",
        answer:
          "Please check with neighbors, building management, your mailbox, and other delivery locations first. If you still cannot find it, contact us within 48 hours so we can help investigate with the carrier.",
      },
      {
        category: "returns",
        question: "What is your return policy?",
        answer:
          "Most eligible items can be returned within 30 days of delivery. Items must be unused, in original packaging, and include all tags or labels where applicable.",
      },
      {
        category: "returns",
        question: "How do I return an item?",
        answer:
          "Contact our support team with your order number and return reason. We will provide return instructions. Please use a trackable shipping method when returning your item.",
      },
      {
        category: "returns",
        question: "When will I receive my refund?",
        answer:
          "Refunds are usually processed after we receive and inspect your returned item. Please allow additional time for your bank or payment provider to post the refund.",
      },
      {
        category: "payment",
        question: "What payment methods do you accept?",
        answer:
          "We accept major credit cards, debit cards, and other payment methods shown at checkout. Available payment options may vary depending on your location and device.",
      },
      {
        category: "payment",
        question: "Is it safe to use my credit card?",
        answer:
          "Yes. Payments are processed through secure payment gateways. We do not store your full credit card details.",
      },
      {
        category: "payment",
        question: "My payment was declined.",
        answer:
          "Please check that your card is active, has sufficient funds, and that your billing details match your bank records. You may also try another payment method.",
      },
      {
        category: "account",
        question: "How do I create an account?",
        answer:
          "Click the Sign Up or Account option on the website and enter your details. You may also be able to create an account during checkout.",
      },
      {
        category: "account",
        question: "I forgot my password.",
        answer:
          "Click Forgot Password on the login page and enter your email address. You will receive a reset link if an account exists with that email.",
      },
      {
        category: "account",
        question: "How do I update my account information?",
        answer:
          "Log into your account and go to Account Settings. From there, you can update your name, email, password, shipping address, and other saved details.",
      },
    ],
    []
  );

  const filteredFaqs = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    return faqs.filter((faq) => {
      const matchesCategory =
        activeCategory === "all" || faq.category === activeCategory;

      const matchesSearch =
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query);

      return matchesCategory && matchesSearch;
    });
  }, [faqs, activeCategory, searchQuery]);

  return (
    <section className="help-center">
      <style>
        {`
          .help-center {
            padding: 40px 0;
            background: #fafafa;
          }

          .help-center .help-wrapper {
            margin: 0 auto;
          }

          .help-center .help-header {
            text-align: center;
            margin-bottom: 30px;
          }

          .help-center .help-badge {
            display: inline-block;
            font-size: 13px;
            font-weight: 600;
            color: #0d6efd;
            background: #eef5ff;
            padding: 6px 14px;
            border-radius: 50px;
            margin-bottom: 12px;
          }

          .help-center .help-title {
            font-size: 34px;
            line-height: 1.2;
            margin-bottom: 10px;
            color: #222;
          }

          .help-center .help-description {
            max-width: 620px;
            margin: 0 auto;
            font-size: 15px;
            line-height: 1.6;
            color: #666;
          }

          .help-center .help-search {
            margin-bottom: 18px;
          }

          .help-center .help-search input {
            width: 100%;
            height: 48px;
            border: 1px solid #e5e5e5;
            border-radius: 10px;
            padding: 0 16px;
            font-size: 14px;
            color: #333;
            background: #fff;
            outline: none;
          }

          .help-center .help-search input:focus {
            border-color: #0d6efd;
          }

          .help-center .category-list {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            margin-bottom: 24px;
          }

          .help-center .category-button {
            border: 1px solid #e5e5e5;
            background: #fff;
            color: #555;
            font-size: 14px;
            font-weight: 500;
            padding: 8px 14px;
            border-radius: 50px;
            cursor: pointer;
            transition: 0.2s ease;
          }

          .help-center .category-button.active,
          .help-center .category-button:hover {
            color: #0d6efd;
            border-color: #0d6efd;
            background: #eef5ff;
          }

          .help-center .help-section {
            background: #fff;
            border: 1px solid #eee;
            border-radius: 12px;
            padding: 22px;
            margin-bottom: 16px;
          }

          .help-center .help-section-title {
            font-size: 20px;
            line-height: 1.3;
            margin-bottom: 16px;
            color: #222;
            font-weight: 700;
          }

          .help-center .faq-item {
            border-bottom: 1px solid #eee;
            padding-bottom: 16px;
            margin-bottom: 16px;
          }

          .help-center .faq-item:last-child {
            border-bottom: 0;
            padding-bottom: 0;
            margin-bottom: 0;
          }

          .help-center .faq-question {
            font-size: 16px;
            line-height: 1.4;
            color: #222;
            font-weight: 700;
            margin-bottom: 6px;
          }

          .help-center .faq-answer {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 0;
          }

          .help-center .no-results {
            text-align: center;
            padding: 30px 20px;
            background: #fff;
            border: 1px solid #eee;
            border-radius: 12px;
            margin-bottom: 16px;
          }

          .help-center .no-results h4 {
            font-size: 18px;
            color: #222;
            margin-bottom: 8px;
          }

          .help-center .no-results p {
            font-size: 14px;
            color: #666;
            margin-bottom: 16px;
          }

          .help-center .clear-button {
            border: 0;
            background: #0d6efd;
            color: #fff;
            font-size: 14px;
            font-weight: 600;
            padding: 9px 18px;
            border-radius: 50px;
            cursor: pointer;
          }

          .help-center .contact-box {
            background: #eef5ff;
            border: 1px solid #d9e8ff;
            border-radius: 12px;
            padding: 22px;
            margin-top: 24px;
            text-align: center;
          }

          .help-center .contact-box h3 {
            font-size: 20px;
            color: #222;
            margin-bottom: 8px;
          }

          .help-center .contact-box p {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 12px;
          }

          .help-center .contact-link {
            color: #0d6efd;
            font-weight: 700;
            text-decoration: none;
          }

          .help-center .resource-links {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 14px;
            margin-top: 18px;
          }

          .help-center .resource-links a {
            font-size: 14px;
            color: #666;
            text-decoration: none;
          }

          .help-center .resource-links a:hover {
            color: #0d6efd;
          }

          @media (max-width: 575px) {
            .help-center {
              padding: 30px 0;
            }

            .help-center .help-title {
              font-size: 28px;
            }

            .help-center .help-section {
              padding: 18px;
            }

            .help-center .help-section-title {
              font-size: 18px;
            }

            .help-center .category-list {
              gap: 8px;
            }

            .help-center .category-button {
              font-size: 13px;
              padding: 7px 12px;
            }
          }
        `}
      </style>

      <div className="container">
        <div className="help-wrapper">
          <div className="help-header">
            <span className="help-badge">{header.badge}</span>

            <h1 className="help-title">{header.title}</h1>

            <p className="help-description">{header.description}</p>
          </div>

          <div className="help-search">
            <input
              type="text"
              value={searchQuery}
              placeholder="Search for help..."
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="category-list">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`category-button ${
                  activeCategory === category.id ? "active" : ""
                }`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>

          <div className="help-section">
            <h3 className="help-section-title">Frequently Asked Questions</h3>

            {filteredFaqs.length === 0 ? (
              <div className="no-results">
                <h4>No results found</h4>
                <p>Try another keyword or browse all topics.</p>

                <button
                  type="button"
                  className="clear-button"
                  onClick={() => {
                    setSearchQuery("");
                    setActiveCategory("all");
                  }}
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              filteredFaqs.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h4 className="faq-question">{faq.question}</h4>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))
            )}
          </div>

          <div className="contact-box">
            <h3>Still Need Help?</h3>
            <p>
              Contact our support team and we will help you with your order,
              shipping, return, payment, or account question.
            </p>

            <a href="/contact" className="contact-link">
              Contact Support
            </a>

            <div className="resource-links">
              <a href="/terms-conditions">Terms & Conditions</a>
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/refund-policy">Refund Policy</a>
              <a href="/shipping-info">Shipping Information</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HelpCenter;